/**
 * ServiceHistoryTable Component
 * Displays all service history from all trucks in date order
 * Automatically updates when service entries are added/modified
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  Button,
  CircularProgress,
} from '@mui/material';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import type { Truck } from '../types';
import type { Customer } from '../../../../types/customer';
import type { InvoiceFormData } from '../../../../types/invoice.types';
import { formatServiceDate } from '../utils/formatters';
import { useCreatePayPalInvoice } from '../../../admin/invoices/hooks/usePayPalInvoices';
import { getTodayDate } from '../../timeline/utils';
import { generateInvoiceNumber } from '../../../../utils/invoiceNumber';

interface ServiceHistoryTableProps {
  trucks: Truck[];
  customer: Customer;
}

interface ServiceRow {
  serviceId: string;
  truckId: string;
  truckName: string;
  vin: string;
  licensePlate: string;
  serviceDate: string;
  status: 'pass' | 'fail';
  invoiceNumber?: string;
}

/**
 * Prepares invoice data from selected services
 * Each service becomes one line item with formatted description
 */
const prepareInvoiceData = (
  selectedServiceIds: Set<string>,
  allServiceHistory: ServiceRow[],
  customer: Customer
): Partial<InvoiceFormData> => {
  const selectedServices = allServiceHistory.filter(service =>
    selectedServiceIds.has(service.serviceId)
  );

  const lineItems = selectedServices.map(service => {
    const serviceDate = new Date(service.serviceDate);
    const formattedDate = serviceDate.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    return {
      name: 'Carb Check Service',
      description: `Truck: ${service.truckName}, VIN: ${service.vin}, Plate: ${service.licensePlate}, Status: ${service.status.toUpperCase()}, Service Date: ${formattedDate}`,
      quantity: 1,
      rate: 75,
    };
  });

  // ✅ STANDARDIZED INVOICE NUMBER GENERATION
  // Using generateInvoiceNumber() from /src/utils/invoiceNumber.ts
  // Format: INV-XXX-XXX (e.g., INV-234-567)
  const invoiceNumber = generateInvoiceNumber();

  return {
    invoiceNumber, // Unique invoice number in format INV-###-###
    clientId: customer.id, // MongoDB ObjectId of the customer (client)
    ccEmails: [],
    issueDate: getTodayDate(), // Always use PST/PDT timezone for today's date
    paymentTerms: 0, // "Due on Receipt" - matches PayPal form default
    lineItems,
    taxRate: 0,
    discountAmount: 0,
    currency: 'USD',
    memo: 'Thank you for choosing us for your Clean Truck Check testing. We appreciate your trust and the opportunity to help keep your vehicles compliant, reliable, and on the road while supporting cleaner air in our communities. If you have any questions or need assistance with deadlines or re-testing, we\'re here to help.',
    notes: 'Scope: These Terms govern our emissions compliance testing under CARB\'s Clean Truck Check (HD I/M). Info: Clean Truck Check – Overview.\nStatus: We are independent of CARB and do not represent CARB.\nCompliance: We follow CARB rules and guidance and updates.\nCredentials: Tests are performed only by CARB‑credentialed testers who passed the CARB exam (≥80%) and maintain biennial renewal. Biennial renewal.\nProcedures: We use maintained/calibrated equipment and follow CARB/OEM procedures and safety standards.\nApplicability: We verify the appropriate method (e.g., OBD) before testing.\nSubmissions: We submit passing results to CARB/CTC‑VIS when applicable; up to 90 days before a Client\'s deadline.\nClient Responsibility: Clients remain responsible for compliance, paying CARB\'s annual fee in CTC‑VIS and meeting deadlines (incl. NST 30‑day).\nNo Outcome Guarantee: We do not guarantee a pass, CARB acceptance, registration release, or avoidance of enforcement.\nFees: Our fees are separate from CARB fees. We disclose pricing, payment terms, taxes, and any re‑test/expedite/no‑show charges.\nScheduling: We endeavor to meet deadlines but may reschedule for safety/equipment/regulatory reasons. Expedited service does not ensure approval.\nData & Consent: Client authorizes collection and submission of vehicle/owner/test data to CARB/CTC‑VIS.\nPrivacy: We employ reasonable safeguards and notify Clients of required incidents. We limit access and use encryption when feasible.\nRecords: We retain test/calibration/credential/submission records for ≥3 years (or longer if required) and cooperate with lawful audits.\nProhibited Conduct: No falsification or manipulation of tests/data; no unauthorized CTC‑VIS access.\nInsurance: We maintain commercially reasonable insurance and safe operations.\nWarranties: Services are provided "as is." Implied warranties are disclaimed to the extent permitted by law.\nLiability: Our total liability is limited to fees paid for the specific service; no indirect/incidental/consequential damages.\nIndemnity: Client indemnifies us for losses arising from Client\'s non‑compliance (except our willful misconduct or fraud).\nSuspension/Termination: We may refuse/suspend service for nonpayment, unsafe/illegal conditions, or policy violations.\nChanges: We may update procedures/Terms for CARB changes; notice when feasible.\nGoverning Law: California law governs; optional good‑faith mediation before litigation.\nCARB Clean Truck Check hdim@arb.ca.gov • (866) 634‑3735',
  };
};

export const ServiceHistoryTable: React.FC<ServiceHistoryTableProps> = ({ trucks, customer }) => {
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const createInvoiceMutation = useCreatePayPalInvoice();

  // Flatten all service history from all trucks and sort by date
  const allServiceHistory = useMemo(() => {
    const services: ServiceRow[] = [];

    trucks.forEach((truck) => {
      if (truck.serviceHistory && truck.serviceHistory.length > 0) {
        truck.serviceHistory.forEach((service) => {
          services.push({
            serviceId: service.id,
            truckId: truck.id,
            truckName: truck.name || '-',
            vin: truck.vin || '-',
            licensePlate: truck.licensePlate || '-',
            serviceDate: service.serviceDate,
            status: service.status,
            invoiceNumber: service.notes || '-', // TODO: Replace with actual invoice field when available
          });
        });
      }
    });

    // Sort by date, most recent first
    return services.sort((a, b) =>
      new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime()
    );
  }, [trucks]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedServices(new Set(allServiceHistory.map(s => s.serviceId)));
    } else {
      setSelectedServices(new Set());
    }
  };

  const handleSelectOne = (serviceId: string) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  const handleCreateInvoice = async () => {
    if (selectedServices.size === 0) return;

    try {
      const invoiceData = prepareInvoiceData(selectedServices, allServiceHistory, customer);

      // Create draft invoice in PayPal via backend
      const createdInvoice = await createInvoiceMutation.mutateAsync(invoiceData);

      // Navigate to edit the newly created draft invoice
      navigate(`/admin/invoices/${createdInvoice.id}`);
    } catch (error) {
      console.error('Failed to create invoice:', error);
      // Show error to user
      alert('Failed to create invoice. Please try again.');
    }
  };

  return (
    <Box sx={{ px: 3, pb: 3 }}>
      {allServiceHistory.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            color: '#6b7280',
            textAlign: 'center',
            py: 3,
          }}
        >
          No service history available
        </Typography>
      ) : (
        <>
          {/* Create Invoice Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={createInvoiceMutation.isPending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <ReceiptIcon />}
              disabled={selectedServices.size === 0 || createInvoiceMutation.isPending}
              onClick={handleCreateInvoice}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {createInvoiceMutation.isPending ? 'Creating Invoice...' : 'Create Invoice'}
            </Button>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedServices.size > 0 && selectedServices.size < allServiceHistory.length}
                    checked={allServiceHistory.length > 0 && selectedServices.size === allServiceHistory.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Truck Name</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>VIN</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>License Plate</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allServiceHistory.map((service) => (
                <TableRow
                  key={service.serviceId}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedServices.has(service.serviceId)}
                      onChange={() => handleSelectOne(service.serviceId)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    {formatServiceDate(service.serviceDate)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    {service.truckName}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem', color: '#1f2937', fontFamily: 'monospace' }}>
                    {service.vin}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem', color: '#1f2937', fontFamily: 'monospace' }}>
                    {service.licensePlate}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.status.toUpperCase()}
                      sx={{
                        backgroundColor: service.status === 'pass' ? '#10b981' : '#ef4444',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    {service.invoiceNumber}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography
            variant="body2"
            sx={{
              mt: 2,
              fontSize: '0.875rem',
              color: '#6b7280',
            }}
          >
            Showing {allServiceHistory.length} service record{allServiceHistory.length === 1 ? '' : 's'} across all trucks
          </Typography>
        </>
      )}
    </Box>
  );
};
